/**
 * FamilyDashBoard v14 — Main Entry Point
 *
 * Imports all modules, initializes the dashboard.
 */

// ── Styles ──
import "./styles/tokens.css";
import "./styles/themes.css";
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/components.css";
import "./styles/inline-utils.css"; // v13.12.0: utility classes extracted from index.html inline styles
import "./styles/scroll.css";
import "./styles/animations.css";
import "./styles/transitions.css"; // ADR-022 companion: VT Level 2 named transitions (v12.0)
import "./styles/screen-modes.css";
import "./styles/maximize.css";
import "./styles/a11y.css";
import "./styles/print.css";
import "./styles/scope.css"; // ADR-022: @scope per-card isolation (v12.0)
import "./cards/tasks/tasks.css";
import "./cards/system-info/system-info.css";
import "./cards/countdown/countdown.css";

// ── Core ──
import { diagLog, getDiagEntries } from "./core/diag";
import { cEvict, hydrateFromIdb, migrateLocalStorageToIdb, cEvictIdb } from "./core/cache";
import { initVisibility } from "./core/idle";
import { registerSW, unregisterSW } from "./core/sw-register";
import { loadConfig, saveConfig, loadConfigFromHash } from "./core/config";
import { ECFG_PREFIX } from "./core/config-crypto";
import { applyInterfaceLanguage, t } from "./core/i18n";
import { MS_PER_MIN } from "./core/constants";
import { today, nowMs, nowISO } from "./core/temporal";
import { state } from "./core/state";

// ── UI ──
import { initTheme, checkAutoTheme } from "./ui/theme";
import { initKeyboard, registerKey, closeAllOverlays, getKeyboardActions } from "./ui/keyboard";
import { buildHelpRows, sortKeyEntries } from "./core/keymap";
import { initHeader, toggleClockSeconds } from "./ui/header";
import { initCardMaximize, initCardCollapse } from "./ui/maximize";
import { initCardAutoScroll } from "./ui/card-auto-scroll";
import { initStatusBar, stampRefresh } from "./ui/status-bar";
import { initTicker, applyTickerSpeed } from "./ui/ticker";
import { initTodayPane } from "./ui/today-pane";
import {
  initConfigPanel,
  toggleConfigPanel,
  openConfigPanel,
  switchCfgTab,
  openEcfgImportDialog,
} from "./ui/config-panel";
import { initScreenMode, stepFontScale } from "./ui/screen-mode";
import {
  toggleNightDim,
  initNightDimmer,
  setWarmTint,
  isWarmTint,
  setIdleAutoDimMinutes,
} from "./ui/night-dimmer";
import { initDiagOverlay, toggleDiagOverlay } from "./ui/diag-overlay";
import { initProviderDegradationToasts } from "./core/provider-toast";
import { initHealthProbe } from "./core/health-probe";
import { resetGovernor } from "./core/refresh-governor";
import { updateFreshnessClasses } from "./core/sync";
import { startConfigAutoBackup } from "./core/config-backup";
import { initBgImages } from "./ui/bg-images";
import { initCardDragDrop } from "./ui/layout-drag";
import { initCardQuickToggle } from "./ui/card-quick-toggle";
import { initResizers } from "./ui/resizer";
import { showToast } from "./ui/toast";
import { initOfflineBanner } from "./ui/offline-banner";
import { toggleFocusMode, isFocusMode } from "./ui/focus-mode";
import { globalOffline } from "./core/event-bus";
import { initScrollShadows } from "./ui/scroll";
import { mountRegisteredCards } from "./core/card-registry";
import { initCardSettingsButtons } from "./ui/card-settings-dialog";
import { copyFocusedCardPayload } from "./core/semantic-clipboard";

// ── Cards ──
import { initWeatherCard, toggleTempUnit } from "./cards/weather/weather";
import { initMotivationCard, renderMotivation } from "./cards/motivation/motivation";
import { initNewsCard, toggleBookmarkMode, markAllRead } from "./cards/news/news";
import { initStocksCard } from "./cards/stocks/stocks";
import { initCurrencyCard } from "./cards/currency/currency";
import {
  initAlertsCard,
  setAlertsEnabled,
  setAlertsRealtime,
  toggleAlerts,
  isAlertsEnabled,
  setAlertVolume,
} from "./cards/alerts/alerts";
import { initHebrewCalCard } from "./cards/hebrew-cal/hebrew-cal";
import { initCalendarCard } from "./cards/calendar/calendar";
import { initTasksCard } from "./cards/tasks/tasks";
import { initSystemInfoCard } from "./cards/system-info/system-info";
// Eagerly define <fdb-video-news> custom element so it upgrades when index.html is parsed
import "./cards/video-news/fdb-video-news";

import { installGlobalErrorHandlers } from "./core/error-tracker";
import { withErrorBoundary } from "./core/error-boundary";
import {
  initPerfObserver,
  markDomReady,
  markStartupComplete,
  recordCardInitTime,
} from "./core/perf";
import { applyHardwareTier } from "./core/hardware";
import { applyConfigAnimLevel } from "./core/anim-level";
import { scheduleVitalsReport, flushVitalsReport } from "./core/vitals-reporter";
import { initTour } from "./core/first-run-tour";
import { downloadSnapshot } from "./core/snapshot";

// ── Version ──
export const VERSION = __APP_VERSION__;

// Install error handlers + perf observer as early as possible (before init)
installGlobalErrorHandlers();
initPerfObserver();
// Record DOMContentLoaded mark for startup waterfall measurement (v8.2)
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", markDomReady, { once: true });
} else {
  markDomReady();
}
// Detect hardware tier and apply data-hw-tier to <html> for adaptive CSS
applyHardwareTier();
// Schedule Web Vitals report 30 s after boot (v11.0-OBS-1)
scheduleVitalsReport();
// Flush vitals on page hide so we capture them before unload
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") flushVitalsReport();
});
// Show first-run tour on first visit (v11.0-PWA-1)
initTour();

/**
 * Apply card size overrides from config to DOM elements.
 * Called on init so sizes persist across page reloads.
 */
export function applyCardSizes(cardSizes: Record<string, string>): void {
  Object.entries(cardSizes).forEach(([id, size]) => {
    const el = document.querySelector<HTMLElement>(`[data-card-id="${id}"]`);
    if (el) el.dataset["cardSize"] = size;
  });
}

/**
 * Hide/show cards based on the `hiddenCards` array in config.
 * Matches `[data-card-id]` elements in the DOM and toggles `display:none`.
 */
export function applyHiddenCards(hiddenCards: string[]): void {
  document.querySelectorAll<HTMLElement>("[data-card-id]").forEach((el) => {
    const id = el.dataset["cardId"] ?? "";
    el.style.display = hiddenCards.includes(id) ? "none" : "";
  });
}

/**
 * Re-parent cards to the correct DOM column per the saved layout.
 * `layout[0]` = col-left ids, `layout[1]` = col-mid ids, `layout[2]` = col-right ids.
 * Each id is matched against `[data-card-id]` and appended to the target column.
 * Missing ids (unregistered) are silently ignored.
 */
export function applyCardLayout(layout: [string[], string[], string[]] | null): void {
  if (!layout) return;
  const cols = [
    document.querySelector<HTMLElement>(".grid-col-left"),
    document.querySelector<HTMLElement>(".grid-col-mid"),
    document.querySelector<HTMLElement>(".grid-col-right"),
  ];
  layout.forEach((ids, colIdx) => {
    const col = cols[colIdx];
    if (!col) return;
    for (const id of ids) {
      const card = document.querySelector<HTMLElement>(`[data-card-id="${id}"]`);
      if (card) col.appendChild(card);
    }
  });
}

/**
 * Apply a seasonal CSS class to <body> based on Northern Hemisphere months.
 * Months (0-based): spring=2–4, summer=5–7, autumn=8–10, winter=11/0/1.
 */
export function applySeasonClass(): void {
  const m = today().getMonth();
  const cls =
    m >= 2 && m <= 4
      ? "season-spring"
      : m >= 5 && m <= 7
        ? "season-summer"
        : m >= 8 && m <= 10
          ? "season-autumn"
          : "season-winter";
  document.body.classList.remove(
    "season-spring",
    "season-summer",
    "season-autumn",
    "season-winter",
  );
  document.body.classList.add(cls);
}

let countdownInitPromise: Promise<() => void> | null = null;

function getCountdownInit(): Promise<() => void> {
  countdownInitPromise ??= import("./cards/countdown/countdown").then(
    ({ initCountdownCard }) => initCountdownCard,
  );
  return countdownInitPromise;
}

/** Refresh every card individually with 350 ms stagger — never reloads the page. */
export function refreshAllCardsStaggered(): void {
  resetGovernor(); // Allow all cards to re-render regardless of cached hash
  const inits: Array<() => void | Promise<void>> = [
    initWeatherCard,
    initNewsCard,
    initAlertsCard,
    initHebrewCalCard,
    initCalendarCard,
    initStocksCard,
    initCurrencyCard,
    initTasksCard,
    async () => (await getCountdownInit())(),
    initMotivationCard,
    initSystemInfoCard,
  ];
  inits.forEach((fn, i) => {
    setTimeout(() => {
      void fn();
    }, i * 350);
  });
}

/**
 * Application initialization.
 */
export function init(): void {
  diagLog(`[init] FDB-001: FamilyDashBoard v${VERSION} starting...`);

  // Seed reactive state store with current config on startup (v8.0)
  const _initCfg = loadConfig();
  state.seedConfig(_initCfg as unknown as Record<string, unknown>);
  applyInterfaceLanguage(_initCfg.interfaceLanguage);

  // Core setup — evict stale LS entries and hydrate memory cache from IDB
  cEvict();
  void hydrateFromIdb().then((n) => {
    if (n > 0) diagLog(`[cache] FDB-004: hydrated ${n} entries from IDB`);
  });
  // One-time LS→IDB migration; evict stale IDB entries
  void migrateLocalStorageToIdb().then((n) => {
    if (n > 0) diagLog(`[cache] FDB-005: migrated ${n} entries LS→IDB`);
  });
  void cEvictIdb();
  applySeasonClass();
  initVisibility();

  // UI modules
  initTheme();
  initScreenMode();
  initKeyboard();
  initHeader();
  initBgImages();
  initCardMaximize();
  initCardCollapse();
  initCardAutoScroll();
  initCardDragDrop();
  initResizers();
  initStatusBar();
  initTodayPane();
  initConfigPanel();
  initDiagOverlay();
  initProviderDegradationToasts();
  initHealthProbe();
  initScrollShadows();
  // auto-mount registry cards not already in index.html (e.g. video-news)
  mountRegisteredCards();
  // Per-card settings gear buttons (lazy async — fires after card shells are in DOM)
  void initCardSettingsButtons();
  initCardQuickToggle();

  // Add aria-label to icon-only collapse buttons
  document.querySelectorAll<HTMLButtonElement>(".card-collapse-btn").forEach((btn) => {
    if (!btn.getAttribute("aria-label")) {
      btn.setAttribute("aria-label", "מזער/הרחב כרטיסית");
    }
  });

  // ── Additional keyboard shortcuts ──
  registerKey(
    "s",
    document.documentElement.lang === "en" ? "Settings" : "הגדרות",
    toggleConfigPanel,
  );
  registerKey(
    "n",
    document.documentElement.lang === "en" ? "Night dimmer" : "דימר לילה",
    toggleNightDim,
  );
  registerKey(
    "c",
    document.documentElement.lang === "en" ? "Seconds" : "שניות",
    toggleClockSeconds,
  );
  registerKey("+", document.documentElement.lang === "en" ? "Increase font" : "הגדל גופן", () =>
    stepFontScale(1),
  );
  registerKey("=", document.documentElement.lang === "en" ? "Increase font" : "הגדל גופן", () =>
    stepFontScale(1),
  ); // + without shift
  registerKey("-", document.documentElement.lang === "en" ? "Decrease font" : "הקטן גופן", () =>
    stepFontScale(-1),
  );
  registerKey("f", document.documentElement.lang === "en" ? "Fullscreen" : "מסך מלא", () => {
    if (!document.fullscreenElement) void document.documentElement.requestFullscreen();
    else void document.exitFullscreen();
  });
  registerKey("b", document.documentElement.lang === "en" ? "Bookmarks" : "מועדפים", () =>
    toggleBookmarkMode(),
  );
  // P3: mark all news items as read
  registerKey("x", document.documentElement.lang === "en" ? "Mark all read" : "סמן הכל כנקרא", () =>
    markAllRead(),
  );
  registerKey("m", document.documentElement.lang === "en" ? "Next quote" : "ציטוט הבא", () =>
    renderMotivation(),
  );
  registerKey("r", document.documentElement.lang === "en" ? "Refresh data" : "רענון נתונים", () =>
    refreshAllCardsStaggered(),
  );
  registerKey("w", document.documentElement.lang === "en" ? "Toggle °C/°F" : "מעבר °C/°F", () =>
    toggleTempUnit(),
  );
  // X15: semantic clipboard — yank focused card's payload
  registerKey("y", document.documentElement.lang === "en" ? "Copy semantic" : "העתק תוכן", () => {
    void copyFocusedCardPayload().then((cardId) => {
      if (cardId) {
        showToast(document.documentElement.lang === "en" ? "Copied" : "הועתק לזיכרון", 1500);
      }
    });
  });
  registerKey("1", "עיר מזג אוויר 1", () =>
    document.querySelector<HTMLButtonElement>(".wx-city-tab[data-city='1']")?.click(),
  );
  registerKey("2", "עיר מזג אוויר 2", () =>
    document.querySelector<HTMLButtonElement>(".wx-city-tab[data-city='2']")?.click(),
  );
  registerKey("3", "עיר מזג אוויר 3", () =>
    document.querySelector<HTMLButtonElement>(".wx-city-tab[data-city='3']")?.click(),
  );
  registerKey(
    "a",
    document.documentElement.lang === "en" ? "Red alerts" : "התרעות צבע אדום",
    () => {
      toggleAlerts();
      showToast(isAlertsEnabled() ? t("alertsEnabled") : t("alertsDisabled"), 2500);
    },
  );
  const _toggleHelp = (): void => {
    const dlg = document.getElementById("help-overlay") as HTMLDialogElement | null;
    if (!dlg) return;
    if (dlg.open) {
      dlg.close();
    } else {
      // Populate dynamic shortcuts from keymap registry
      const dynamicEl = document.getElementById("help-dynamic-keys");
      if (dynamicEl) {
        const lang = document.documentElement.lang === "en" ? "en" : "he";
        const sorted = sortKeyEntries(getKeyboardActions());
        dynamicEl.replaceChildren(buildHelpRows(sorted, lang));
      }
      dlg.showModal();
    }
  };
  registerKey("h", document.documentElement.lang === "en" ? "Help" : "עזרה", _toggleHelp);
  registerKey("?", document.documentElement.lang === "en" ? "Help" : "עזרה", _toggleHelp);
  registerKey(
    "d",
    document.documentElement.lang === "en" ? "Diagnostics" : "אבחון",
    toggleDiagOverlay,
  );
  // Ctrl+Shift+E — export diagnostic log as JSON file
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === "E") {
      e.preventDefault();
      const entries = getDiagEntries(500);
      const json = JSON.stringify({ exported: nowISO(), entries }, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fdb-diag-${nowMs()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  });
  // Ctrl+Shift+S — export full dashboard snapshot (X8)
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === "S") {
      e.preventDefault();
      downloadSnapshot();
      showToast(document.documentElement.lang === "en" ? "Snapshot exported" : "תצלום יוצא", 2500);
      diagLog("[snapshot] Ctrl+Shift+S snapshot downloaded");
    }
  });
  registerKey(
    "v",
    document.documentElement.lang === "en" ? "Card management" : "ניהול כרטיסיות",
    () => {
      openConfigPanel();
      switchCfgTab("cards");
    },
  );
  registerKey(
    "l",
    document.documentElement.lang === "en" ? "Warm night tint" : "גוון חם לדימר לילה",
    () => setWarmTint(!isWarmTint()),
  );
  registerKey("g", document.documentElement.lang === "en" ? "Focus mode" : "מצב מיקוד", () => {
    toggleFocusMode();
    showToast(isFocusMode() ? "מצב מיקוד פעיל" : "מצב רגיל", 1500);
  });
  registerKey(
    "escape",
    document.documentElement.lang === "en" ? "Close overlays" : "סגור כל חלון",
    closeAllOverlays,
  );

  // Cards — priority-based init: high-value visible cards first (v7.10)
  // wrap each init with timing; error-boundary catches init failures
  const timedInit = (id: string, fn: () => void | Promise<void>): void => {
    const t0 = performance.now();
    void withErrorBoundary(id, fn)().then(() => {
      recordCardInitTime(id, performance.now() - t0);
    });
  };
  // HIGH priority: user-visible, time-sensitive data
  timedInit("weather", initWeatherCard);
  timedInit("news", initNewsCard);
  timedInit("alerts", initAlertsCard);
  timedInit("hebrew-cal", initHebrewCalCard);
  timedInit("calendar", initCalendarCard);
  // NORMAL priority: financial data + tasks
  timedInit("stocks", initStocksCard);
  timedInit("currency", initCurrencyCard);
  timedInit("tasks", initTasksCard);
  timedInit("countdown", async () => (await getCountdownInit())());
  // LOW priority: ambient / decorative — deferred to reduce TTI (V11-PERF)
  // Use requestIdleCallback when available so the main thread is free for interaction first.
  const _lowPriorityInit = (): void => {
    timedInit("motivation", initMotivationCard);
    timedInit("system-info", initSystemInfoCard);
    initTicker();
  };
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(_lowPriorityInit, { timeout: 2000 });
  } else {
    setTimeout(_lowPriorityInit, 200);
  }

  // Mark startup waterfall completion (v8.2: all card init calls dispatched)
  markStartupComplete();

  // ── URL hash config import: #cfg=<base64> overrides localStorage config ──
  const _urlHash = window.location.hash ?? "";
  if (_urlHash.startsWith("#cfg=")) {
    const imported = loadConfigFromHash(_urlHash);
    if (imported) {
      saveConfig(imported);
      // Strip hash so the next reload doesn't re-import
      history.replaceState(null, "", window.location.pathname + window.location.search);
      diagLog("[init] FDB-007: config imported from URL hash");
    }
  }
  // ── Encrypted config URL import (#ecfg= fragment) ── // owasp-allow:A08 — dynamic import is for config URL fragment, not user-controlled module path
  if (_urlHash.startsWith(ECFG_PREFIX)) {
    openEcfgImportDialog(_urlHash);
  }

  // ── Night dimmer auto-schedule (uses config v2 schedule fields) ──
  const cfg = _initCfg; // reuse config already loaded at init start — avoid second LS read
  initNightDimmer(
    cfg.nightDimLevel,
    cfg.nightDimScheduleEnabled ?? false,
    cfg.nightDimStartHour ?? 23,
    cfg.nightDimEndHour ?? 6,
  );
  if ((cfg.nightDimIdleMinutes ?? 0) > 0) {
    setIdleAutoDimMinutes(cfg.nightDimIdleMinutes ?? 0);
  }

  // ── Apply ticker scroll speed from config ──
  applyTickerSpeed(cfg.tickerSpeed ?? 3);

  // ── Apply card visibility from config ──
  applyHiddenCards(cfg.hiddenCards ?? []);

  // ── Apply saved card layout column assignment ──
  applyCardLayout(cfg.cardLayout ?? null);

  // ── Apply saved card size overrides (sm/md/lg/xl) ──
  applyCardSizes(cfg.cardSizes ?? {});

  // ── Apply alert config state from saved settings ──
  setAlertsEnabled(cfg.alertsEnabled);
  setAlertsRealtime(cfg.realtimeAlerts);
  setAlertVolume(cfg.alertVolume ?? 18);

  // ── Apply warm tint from config ──
  if (cfg.dimWarmTint) setWarmTint(true);

  // ── Apply animation level from config ──
  applyConfigAnimLevel(cfg);

  // ── Auto-theme by time of day (runs every 5 minutes) — deferred to reduce TTI ──
  const _autoThemeSetup = (): void => {
    const runAutoTheme = (): void => {
      const c = loadConfig();
      checkAutoTheme(c.autoTheme, c.theme);
    };
    runAutoTheme();
    setInterval(runAutoTheme, 5 * MS_PER_MIN);
  };
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(_autoThemeSetup, { timeout: 3000 });
  } else {
    setTimeout(_autoThemeSetup, 300);
  }

  // S59: Update card freshness indicators every 60 seconds
  setInterval(updateFreshnessClasses, MS_PER_MIN);

  // S60: Auto-backup config to IndexedDB every 5 minutes
  startConfigAutoBackup();

  // Wire notification bell (replaces inline onclick="requestNotifPermission()")
  document.getElementById("notif-bell")?.addEventListener("click", () => {
    void Notification.requestPermission();
  });

  // Stamp initial refresh time
  stampRefresh();

  // Service Worker
  void registerSW(refreshAllCardsStaggered);

  // Dev escape hatch: expose `__fdbUnregisterSW()` on globalThis so users
  // behind a corporate proxy can detach a stale SW from DevTools without
  // navigating Application → Service Workers. See sw-register.ts.
  (globalThis as unknown as { __fdbUnregisterSW?: () => Promise<number> }).__fdbUnregisterSW =
    unregisterSW;

  // ── Network reconnect: auto-refresh after connectivity loss ──
  initOfflineBanner(refreshAllCardsStaggered);
  // Also listen to SW NETWORK_BACK broadcast
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
      const data = e.data as { type?: string };
      if (data?.type === "NETWORK_BACK" && !globalOffline.value) {
        showToast(t("onlineRefreshing"), 2500);
        setTimeout(refreshAllCardsStaggered, 500);
      }
    });
  }

  // ── D1/X11: MCP bridge (ADR-066) — lazy-load ONLY when ?mcp=1 on initial page load ──
  // Zero bytes parsed when the flag is absent; privacy by design.
  if (new URLSearchParams(window.location.search).has("mcp")) {
    void import("./core/mcp-bridge").then((m) => m.initMcpBridge());
  }

  diagLog(`[init] FDB-010: dashboard initialized`);
}

// ── Bootstrap ──
// Skip auto-init in Vitest to prevent side effects (intervals, fetch calls)
// from leaking into test environments and causing hangs.
if (!import.meta.env.VITEST) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}
